import {
  generateConferenceCalls,
  generateDividendMessage,
  generateEarningsMessage,
  generateStockSplitMessage,
} from '@benzinga/calendar-manager';
import type { TFunction } from 'i18next';
import { DateTime } from 'luxon';
import { StockSplitsSummary, ConferenceCallsSummary, DividendSummary, EarningsSummary, MASummary } from './summaries';

export interface StockInfoMessage {
  type: 'stock-split' | 'dividend' | 'earnings' | 'conference-call' | 'ma';
  className: string;
  content: string | null;
  webcastUrl?: string;
}

const checkIfIsWithinNumberOfDays = (date: string, numberOfDays = 14): boolean => {
  if (!date) return false;
  const now = DateTime.local();
  const datetime = DateTime.fromFormat(date, 'yyyy-MM-dd');
  const timeFromNow = datetime.diff(now.startOf('day'), 'days').days;
  if (Math.abs(timeFromNow) <= numberOfDays) return true;
  return false;
};

export const generateStockInfoMessages = (
  symbol: string,
  companyStandardName?: string | null,
  splitsSummary?: StockSplitsSummary | null,
  dividendSummary?: DividendSummary | null,
  earningsSummary?: EarningsSummary | null,
  conferenceCallsSummary?: ConferenceCallsSummary | null,
  maSummary?: MASummary | null,
  t?: TFunction,
  language?: string,
): StockInfoMessage[] => {
  const messages: StockInfoMessage[] = [];

  if (splitsSummary) {
    const nextDate = splitsSummary.next?.date_ex ?? '';
    const nextRatio = splitsSummary.next?.ratio ?? '';
    const lastDate = splitsSummary.last?.date_recorded ?? '';
    const lastRatio = splitsSummary.last?.ratio ?? '';

    if (splitsSummary.next && checkIfIsWithinNumberOfDays(nextDate)) {
      messages.push({
        type: 'stock-split',
        className: 'stock-splits-info-box-container',
        content: generateStockSplitMessage(symbol, companyStandardName || '', nextRatio, nextDate, t),
      });
    } else if (splitsSummary.last && checkIfIsWithinNumberOfDays(lastDate, 7)) {
      messages.push({
        type: 'stock-split',
        className: 'stock-splits-info-box-container',
        content: generateStockSplitMessage(symbol, companyStandardName || '', lastRatio, lastDate, t),
      });
    }
  }

  if (dividendSummary) {
    const nextDate = dividendSummary.next?.ex_dividend_date ?? '';
    const lastDate = dividendSummary.last?.ex_dividend_date ?? '';

    if (nextDate && checkIfIsWithinNumberOfDays(nextDate)) {
      messages.push({
        type: 'dividend',
        className: 'dividends-info-box-container',
        content: generateDividendMessage(nextDate, t, language),
      });
    } else if (lastDate && checkIfIsWithinNumberOfDays(lastDate, 7)) {
      messages.push({
        type: 'dividend',
        className: 'dividends-info-box-container',
        content: generateDividendMessage(lastDate, t, language),
      });
    }
  }

  if (earningsSummary) {
    const generateEarningsInfoMessage = (earningsSummary: EarningsSummary, timeframe: 'next' | 'last') => {
      const date = earningsSummary?.[timeframe]?.date ?? '';
      const time = earningsSummary?.[timeframe]?.time ?? '';
      const eps = earningsSummary?.[timeframe]?.eps ?? '';
      const revenue = earningsSummary?.[timeframe]?.revenue ?? '';
      const period = earningsSummary?.[timeframe]?.period ?? '';
      const periodYear = earningsSummary?.[timeframe]?.period_year ?? '';

      return generateEarningsMessage(date, time, eps, revenue, period, periodYear, t, language);
    };

    if (earningsSummary.next?.date_confirmed === 1 && checkIfIsWithinNumberOfDays(earningsSummary.next.date)) {
      messages.push({
        type: 'earnings',
        className: 'earnings-info-box-container',
        content: generateEarningsInfoMessage(earningsSummary, 'next'),
      });
    } else if (
      earningsSummary.last?.date_confirmed === 1 &&
      checkIfIsWithinNumberOfDays(earningsSummary.last.date, 7)
    ) {
      messages.push({
        type: 'earnings',
        className: 'earnings-info-box-container',
        content: generateEarningsInfoMessage(earningsSummary, 'last'),
      });
    }
  }

  if (conferenceCallsSummary) {
    const generateConferenceCallInfoMessage = (
      conferenceCallsSummary: ConferenceCallsSummary,
      timeframe: 'next' | 'last',
    ) => {
      const date = conferenceCallsSummary?.[timeframe]?.date;
      const startTime = conferenceCallsSummary?.[timeframe]?.start_time;
      const webcastUrl = conferenceCallsSummary?.[timeframe]?.webcast_url;

      const content = generateConferenceCalls(date ?? null, startTime ?? null, t, language);
      return { content, webcastUrl };
    };

    if (conferenceCallsSummary.next && checkIfIsWithinNumberOfDays(conferenceCallsSummary.next.date, 7)) {
      const { content, webcastUrl } = generateConferenceCallInfoMessage(conferenceCallsSummary, 'next');
      messages.push({
        type: 'conference-call',
        className: 'conference-calls-info-box-container',
        content,
        webcastUrl,
      });
    } else if (conferenceCallsSummary.last && checkIfIsWithinNumberOfDays(conferenceCallsSummary.last.date, 7)) {
      const { content, webcastUrl } = generateConferenceCallInfoMessage(conferenceCallsSummary, 'last');
      messages.push({
        type: 'conference-call',
        className: 'conference-calls-info-box-container',
        content,
        webcastUrl,
      });
    }
  }

  if (maSummary) {
    const message = maSummary.next?.message ?? maSummary.last?.message;
    if (message) {
      if (
        (maSummary.last?.deal_status === 'Completed' &&
          checkIfIsWithinNumberOfDays(maSummary.last.date_completed, 7)) ||
        checkIfIsWithinNumberOfDays(maSummary.last?.date ?? '', 7)
      ) {
        messages.push({
          type: 'ma',
          className: 'ma-info-box-container',
          content: message,
        });
      } else if (
        (maSummary.next?.deal_status === 'Completed' && checkIfIsWithinNumberOfDays(maSummary.next.date_completed)) ||
        checkIfIsWithinNumberOfDays(maSummary.next?.date ?? '')
      ) {
        messages.push({
          type: 'ma',
          className: 'ma-info-box-container',
          content: message,
        });
      }
    }
  }

  return messages;
};
